import { createRouter, createWebHistory } from 'vue-router'
import AdminLayout from '../layouts/AdminLayout.vue'
import ShopLayout from '../layouts/ShopLayout.vue'

// 청크 로딩 에러 재시도 함수 개선
function lazyLoadWithRetry(importFunc, maxRetries = 3) {
    return new Promise((resolve, reject) => {
        let retries = 0;

        function attempt() {
            importFunc()
                .then(resolve)
                .catch((error) => {
                    retries++;
                    console.warn(`🔄 청크 로딩 실패 (${retries}/${maxRetries}):`, error);

                    if (retries < maxRetries) {
                        const delay = Math.min(1000 * Math.pow(2, retries - 1), 5000);
                        console.log(`⏳ ${delay}ms 후 재시도...`);

                        setTimeout(attempt, delay);
                    } else {
                        console.error('❌ 청크 로딩 최종 실패:', error);
                        if ('caches' in window) {
                            caches.keys().then(function(names) {
                                for (let name of names) caches.delete(name);
                            });
                        }
                        window.location.reload(true);
                        reject(error);
                    }
                });
        }

        attempt();
    });
}

// 동적 import를 위한 래퍼 함수
const dynamicImport = (path) => () => lazyLoadWithRetry(() => import(path));

const routes = [

    // 관리자 로그인 페이지만 레이아웃 없이 분리
    {
        path: '/admin/login',
        name: 'AdminLogin',
        component: () => import('../views/admin/Login.vue')
    },

    // 사용자 리다이렉션
    // {
    //     path: '/',
    //     redirect: '/login'
    // },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/shop/Login.vue')
    },
    {
        path: '/signup',
        name: 'signup',
        component: () => import('../views/shop/account/signup.vue')
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('../views/shop/account/ResetPassword.vue')
    },
    // 쇼핑몰 (사용자) 영역 라우트
    {
        path: '/',
        component: ShopLayout,
        children: [ 
            {
                path: 'home',
                name: 'shop-home',
                component: () => import('../views/shop/Home.vue')
            },
            {
                path: 'products',
                name: 'shop-products',
                component: () => import('../views/shop/product/ProductListView.vue')
            },
            {
                path: 'product/:id',
                name: 'product-detail',
                component: () => import('../views/shop/product/ProductDetailView.vue')
            },
            {
                path: 'cart',
                name: 'cart',
                component: () => import('../views/shop/cart/CartPage.vue')
            },
            {
                path: 'checkout',
                name: 'checkout',
                component: () => import('../views/shop/checkout/CheckoutView.vue')
            },
            {
                path: 'ordercomplete/:orderId',
                name: 'ordercomplete',
                component: () => import('../views/shop/ordercomplete/OrderComplete.vue'),
                props: true
            },
            {
                path: '/payment/toss/checkout',
                name: 'TossCheckout',
                component: () => import('@/views/shop/payment/TossCheckout.vue')
            },
            {
                path: '/payment/success',
                name: 'PaymentSuccess',
                component: () => import('@/views/shop/ordercomplete/OrderComplete.vue')
            },
            {
                path: '/payment/fail',
                name: 'PaymentFail',
                component: () => import('@/views/shop/checkout/CheckoutView.vue')
            },
            {
                path: '/new-products',
                name: 'new-products',
                component: () => import('../views/shop/product/NewProductsPage.vue')
            },
            {
                path: '/promotions',
                name: 'promotions',
                component: () => import('../views/shop/product/PromotionsPage.vue')
            },
            {
                path: '/promotions/:id',
                name: 'promotion-detail',
                component: () => import('../views/shop/product/PromotionDetailPage.vue') // 이 페이지는 별도로 구현해야 합니다
            },
            {
                path: '/points-zone',
                name: 'points-zone',
                component: () => import('../views/shop/point/PointsZonePage.vue')
            },
            {
                path: 'mypage',
                name: 'mypage',
                component: () => import('../views/shop/mypage/Mypagemain.vue')
            },
            {
                path: 'mypage/orderhistory',
                name: 'orderhistory',
                component: () => import('../views/shop/mypage/OrderHistory.vue')
            },
            {
                path: 'mypage/return',
                name: 'return',
                component: () => import('../views/shop/mypage/ReturnRequest.vue')
            },
            {
                path: 'mypage/delivery',
                name: 'delivery',
                component: () => import('../views/shop/mypage/Delivery.vue')
            },
            {
                path: 'mypage/wishlist',
                name: 'wishlist',  // 'steamed'에서 'wishlist'로 변경
                component: () => import('../views/shop/mypage/Wishlist.vue')
            },
            {
                path: '/mypage/reorder/:orderNumber',
                name: 'reorder',
                component: () => import('@/views/shop/mypage/Reorder.vue'),
                meta: {
                    alwaysRefresh: true
                }
            },
            {
                path: 'mypage/ledger',
                name: 'ledger',
                component: () => import('../views/shop/mypage/LedgerPage.vue')
            },
            {
                path: 'mypage/cash',
                name: 'doctor-cash',
                component: () => import('../views/shop/mypage/DoctorCashPage.vue')
            },
            {
                path: 'mypage/coupons',
                name: 'coupons',
                component: () => import('../views/shop/mypage/CouponsPage.vue')
            },
            {
                path: 'mypage/deposits',
                name: 'deposits',
                component: () => import('../views/shop/mypage/DepositPage.vue')
            },
            {
                path: 'mypage/points',
                name: 'points',
                component: () => import('../views/shop/mypage/PointsPage.vue')
            },
            {
                path: 'mypage/profile',
                name: 'profile',
                component: () => import('../views/shop/account/EditProfile.vue')
            },
            {
                path: 'mypage/payment-methods',
                name: 'paymentMethods',
                component: () => import('../views/shop/mypage/PaymentMethodsPage.vue')
            },
            {
                path: 'mypage/notification-settings',
                name: 'notificationSettings',
                component: () => import('../views/shop/account/NotificationSettingsPage.vue')
            },
            {
                path: 'mypage/withdrawal',
                name: 'withdrawal',
                component: () => import('../views/shop/account/WithdrawalPage.vue')
            },
            {
                path: '/mypage/faq',
                name: 'mypage-faq',
                component: () => import('../views/shop/support/FaqPage.vue')
            },
            {
                path: '/mypage/notices',
                name: 'mypage-notices',
                component: () => import('../views/shop/support/NoticesPage.vue')
            },
            {
                path: '/mypage/notices/:id',
                name: 'mypage-notice-detail',
                component: () => import('../views/shop/support/NoticeDetailPage.vue')
            },
            {
                path: '/mypage/inquiries',
                name: 'mypage-inquiries',
                component: () => import('../views/shop/support/InquiriesPage.vue')
            },
            {
                path: '/mypage/inquiries/create',
                name: 'mypage-inquiry-create',
                component: () => import('../views/shop/support/CreateInquiryPage.vue')
            },
            {
                path: '/mypage/inquiries/edit/:id',  // 🔥 이 라우트가 있는지 확인
                name: 'mypage-inquiry-edit',
                component: () => import('../views/shop/support/EditInquiryPage.vue')
            },
            {
                path: '/mypage/inquiries/:id',
                name: 'mypage-inquiry-detail',
                component: () => import('../views/shop/support/InquiryDetailPage.vue')
            },
            // 견적서 관련 라우트
            {
                path: '/mypage/quotes',
                name: 'mypage-quotes',
                component: () => import('../views/shop/mypage/QuotesPage.vue')
            },
            {
                path: '/mypage/quotes/:id',
                name: 'mypage-quote-detail',
                component: () => import('../views/shop/mypage/QuoteDetailPage.vue')
            },
            // 견적요청 관련 라우트
            {
                path: '/mypage/quote-requests',
                name: 'mypage-quote-requests',
                component: () => import('../views/shop/mypage/QuoteRequestsPage.vue'),
                meta: { requiresAuth: true }
            },
            {
                path: '/mypage/quote-requests/new',
                name: 'mypage-quote-request-new',
                component: () => import('../views/shop/mypage/QuoteRequestNewPage.vue'),
                meta: { requiresAuth: true }
            },
            {
                path: '/mypage/quote-requests/:id',
                name: 'mypage-quote-request-detail',
                component: () => import('../views/shop/mypage/QuoteRequestDetailPage.vue'),
                meta: { requiresAuth: true }
            },
            // 견적서 주문 페이지
            {
                path: '/quote-order',
                name: 'quote-order',
                component: () => import('../views/shop/quote/QuoteOrderPage.vue'),
                meta: { requiresAuth: true }
            }
        ]
    },

    // 관리자 영역 라우트
    {
        path: '/admin',
        component: AdminLayout,
        children: [

            {
                path: '', // 기본 대시보드는 남겨두고
                name: 'admin-home',
                component: () => import('../views/admin/DashboardHome.vue')
            },

            // 🔥 주문 관리 관련 라우트들 추가
            {
                path: 'shoporders',
                name: 'shoporders',
                component: () => import('../views/admin/shoporders/OrdersList.vue')
            },
            {
                path: 'shoporders/new',
                name: 'newOrders',
                component: () => import('../views/admin/shoporders/NewOrders.vue')
            },
            {
                path: 'shoporders/processing',
                name: 'processingOrders',
                component: () => import('../views/admin/shoporders/ProcessingOrders.vue')
            },
            {
                path: 'shoporders/preparing',
                name: 'preparingShipping',
                component: () => import('../views/admin/shoporders/PreparingShipping.vue')
            },
            {
                path: 'shoporders/shipping',
                name: 'shipping',
                component: () => import('../views/admin/shoporders/Shipping.vue')
            },
            {
                path: 'shoporders/delivered',
                name: 'delivered',
                component: () => import('../views/admin/shoporders/Delivered.vue')
            },
            {
                path: 'shoporders/cancelled',
                name: 'cancelledOrders',
                component: () => import('../views/admin/shoporders/CancelledOrders.vue')
            },

            {
                path: 'products',
                name: 'admin-products',
                component: () => import('../views/admin/ProductManagement.vue')
            },
            //디자인관리
            {
                path: 'design/main',
                name: 'design-main',
                component: () => import('../views/admin/design/MainProductDisplay.vue')
            },
            {
                path: 'design/theme',
                name: 'design-theme',
                component: () => import('../views/admin/design/ThemeLayoutManager.vue')
            },
            {
                path: 'design/banner',
                name: 'design-banner',
                component: () => import('../views/admin/design/BannerManager.vue')
            },
            {
                path: 'design/menu',
                name: 'design-menu',
                component: () => import('../views/admin/design/MenuManager.vue')
            },
            {
                path: 'design/branding',
                name: 'design-branding',
                component: () => import('../views/admin/design/LogoBrandingManager.vue')
            },
            {
                path: 'design/mobile',
                name: 'design-mobile',
                component: () => import('../views/admin/design/MobileDesignManager.vue')
            },
            //환경설정- 기본설정
            {
                path: 'config/basic',
                name: 'config-basic',
                component: () => import('../views/admin/config/basic.vue')
            },
            //환경설정- 사용자 관리
            {
                path: 'config/users',
                name: 'config-users',
                component: () => import('../views/admin/config/users.vue')
            },
            //환경설정- 권한설정
            {
                path: 'config/permissions',
                name: 'config-permissions',
                component: () => import('../views/admin/config/permissions.vue')
            },
            //환경설정- 시스템설정
            {
                path: 'config/system',
                name: 'config-system',
                component: () => import('../views/admin/config/system.vue')
            },

            // 거래처 관리 - 거래처 목록
            {
                path: 'vendor/list',
                name: 'vendor-list',
                component: () => import('../views/admin/vendor/VendorList.vue')
            },
            // 거래처 관리 - 거래처 등록
            {
                path: 'vendor/register',
                name: 'vendor-register',
                component: () => import('../views/admin/vendor/register.vue')
            },
            // 거래처 관리 - 거래처 등급
            {
                path: 'vendor/level',
                name: 'vendor-level',
                component: () => import('../views/admin/vendor/level.vue')
            },
            // 거래처 관리 - 거래처 거래내역
            {
                path: 'vendor/transactions',
                name: 'vendor-transactions',
                component: () => import('../views/admin/vendor/transactions.vue')
            },
            // 제품 관리 - 제품 관리 경로 추가
            {
                path: 'product/list',
                name: 'product-list',
                component: () => import('../views/admin/product/ProductList.vue')
            },
            // 제품 관리 - 제품 등록 경로 추가
            {
                path: 'product/register',
                name: 'product-register',
                component: () => import('../views/admin/product/Productregister.vue')
            },
            // 제품 관리 - 카테고리 경로 추가
            {
                path: 'product/categories',
                name: 'product-categories',
                component: () => import('../views/admin/product/Productcategories.vue')
            },
            // 제품 관리 - 크레텍책임 제품연동
            {
                path: 'product/cretec-sync',
                name: 'product-cretec-sync',
                component: () => import('../views/admin/product/CretecProductSync.vue')
            },
            // 재고관리 - 재고현황
            {
                path: 'inventory/status',
                name: 'inventory-status',
                component: () => import('../views/admin/inventory/InventoryStatus.vue')
            },
            // 🔥 재고관리 - 재고 조정 (추가)
            {
                path: 'inventory/adjust',
                name: 'inventory-adjust',
                component: () => import('../views/admin/inventory/InventoryStatus.vue')
            },
            // 재고관리 - 입고 관리
            {
                path: 'inventory/incoming',
                name: 'inventory-incoming',
                component: () => import('../views/admin/inventory/InventoryIncoming.vue')
            },
            // 재고관리 - 출고 관리
            {
                path: 'inventory/outgoing',
                name: 'inventory-outgoing',
                component: () => import('../views/admin/inventory/InventoryOutbound.vue')
            },
            // 주문 관리 - 주문 목록
            {
                path: 'shoporders/order',
                name: 'shoporders-order',
                component: () => import('../views/admin/shoporders/OrdersList.vue')
            },
            // 주문 관리 - 신규 주문
            {
                path: 'shoporders/neworder',
                name: 'shoporders-neworder',
                component: () => import('../views/admin/shoporders/NewOrders.vue')
            },
            // 주문 관리 - 주문 처리
            // 주문 관리 - 주문 완료
            // 주문 관리 - 주문 취소
            // 주문 관리 - 결제 확인

            // 수주/견적관리 - 수주 관리
            {
                path: 'business/list',
                name: 'business-list',
                component: () => import('../views/admin/business/OrderList.vue')
            },
            // 수주/견적관리 - 견적 목록
            {
                path: 'business/estimates',
                name: 'business-estimates',
                component: () => import('../views/admin/business/Estimates.vue')
            },
            // 견적서관리 - 견적서 목록
            {
                path: 'quotes/list',
                name: 'admin-quotes-list',
                component: () => import('../views/admin/business/Estimates.vue')
            },
            // 수주/견적관리 - 견적 등록
            {
                path: 'business/create',
                name: 'business-create',
                component: () => import('../views/admin/business/EstimatesCreate.vue')
            },
            // 배송/출고관리 - 배송현황
            {
                path: 'shipping/list',
                name: 'shipping-list',
                component: () => import('../views/admin/shipping/ShippingList.vue')
            },
            // 배송/출고관리 - 출고현황
            {
                path: 'shipping/dispatch',
                name: 'shipping-dispatch',
                component: () => import('../views/admin/shipping/DispatchList.vue')
            },
            // 배송/출고관리 - 송장등록
            {
                path: 'shipping/invoice',
                name: 'shipping-invoice',
                component: () => import('../views/admin/shipping/InvoiceRegis.vue')
            },
            // 프로모션 관리 경로 추가
            {
                path: 'promotion/list',
                name: 'promotion-list',
                component: () => import('../views/admin/promotion/PromotionList.vue')
            },
            {
                path: 'promotion/coupons',
                name: 'promotion-coupons',
                component: () => import('../views/admin/promotion/CouponManager.vue')
            },
            // 정산및 외상결제관리
            {
                path: 'payment/settlements',
                name: 'payment-settlements',
                component: () => import('../views/admin/payment/Settlements.vue')
            },
            //  외상결제현황
            {
                path: 'payment/deferred',
                name: 'payment-deferred',
                component: () => import('../views/admin/payment/Deferred.vue')
            },
            //  입금내역
            {
                path: 'payment/deposits',
                name: 'payment-deposits',
                component: () => import('../views/admin/payment/Deposits.vue')
            },
            // 매출/통계
            {
                path: 'stats/sales',
                name: 'stats-sales',
                component: () => import('../views/admin/stats/SalesList.vue')
            },
            // 주문통계
            {
                path: 'stats/statsorders',
                name: 'stats-statsorders',
                component: () => import('../views/admin/stats/StatsOrders.vue')
            },
            // 고객분석
            {
                path: 'stats/customers',
                name: 'stats-customers',
                component: () => import('../views/admin/stats/CustomerStats.vue')
            },
            {
                path: 'support/dashboard',
                name: 'SupportDashboard',
                component: () => import('@/views/admin/support/SupportDashboard.vue')
            },
            {
                path: 'support/notice',
                name: 'NoticeManagement',
                component: () => import('@/views/admin/support/NoticeManagement.vue')
            },
            {
                path: 'support/faq',
                name: 'FaqManagement',
                component: () => import('@/views/admin/support/FaqManagement.vue')
            },
            {
                path: 'support/inquiry',
                name: 'InquiryManagement',
                component: () => import('@/views/admin/support/InquiryManagement.vue')
            },
            {
                path: 'support/reviews',
                name: 'ReviewManagement',
                component: () => import('@/views/admin/support/ReviewManagement.vue')
            },
            {
                path: 'support/feedback',
                name: 'FeedbackManagement',
                component: () => import('@/views/admin/support/FeedbackManagement.vue')
            },
            {
                path: 'support/quote-requests',
                name: 'QuoteRequestsManagement',
                component: () => import('@/views/admin/support/QuoteRequests.vue')
            }
            // 아래 경로들은 나중에 각 파일을 만든 후 주석 해제
            // {
            //   path: 'categories',
            //   name: 'admin-categories',
            //   component: () => import('../views/admin/CategoryManagement.vue')
            // },
            // {
            //   path: 'orders',
            //   name: 'admin-orders',
            //   component: () => import('../views/admin/OrderManagement.vue')
            // },
            // {
            //   path: 'users',
            //   name: 'admin-users',
            //   component: () => import('../views/admin/UserManagement.vue')
            // }
        ]
    }
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    // 🔥 모든 페이지 이동 시 상단으로 스크롤
    scrollBehavior() {
        return { top: 0 };
    }
})
// 여기에 라우터 가드 추가
router.beforeEach((to, from, next) => {
    const userToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');

    console.log('🔍 라우터 가드:', {
        path: to.path,
        userToken: userToken ? '있음' : '없음',
        adminToken: adminToken ? '있음' : '없음'
    });

    // 🔥 1. 루트 경로 처리 (가장 우선)
    if (to.path === '/') {
        if (userToken && !adminToken) {
            // 일반 사용자만 로그인된 경우
            next('/home');
        } else if (adminToken && !userToken) {
            // 관리자만 로그인된 경우
            next('/admin');
        } else if (adminToken && userToken) {
            // 둘 다 로그인된 경우 - 관리자 우선
            next('/admin');  // 🔥 관리자 우선으로 변경
        } else {
            // 둘 다 없으면 로그인 페이지로
            next('/login');
        }
        return;
    }

    // 🔥 2. 관리자 경로 처리
    if (to.path.startsWith('/admin')) {
        if (to.path === '/admin/login') {
            // 관리자 로그인 페이지
            if (adminToken) {
                next('/admin');
            } else {
                next();
            }
            return;
        } else {
            // 기타 관리자 페이지
            if (!adminToken) {
                if (userToken) {
                    alert('⚠️ 관리자 전용 페이지입니다.\n일반 사용자는 접근할 수 없습니다.');
                    next('/admin/login');  // 🔥 이렇게 변경
                } else {
                    alert('⚠️ 관리자 로그인이 필요합니다.');
                    next('/admin/login');
                }
            } else {
                next();
            }
            return;
        }
    }

    // 🔥 3. 퍼블릭 라우트 처리 (로그인, 회원가입 등)
    const publicRoutes = ['/login', '/signup', '/reset-password'];
    if (publicRoutes.includes(to.path)) {
        if (adminToken && !userToken) {
            next('/admin');
        } else if (userToken) {
            next('/home');
        } else {
            next();
        }
        return;
    }

    // 🔥 4. 일반 사용자 보호 영역 (로그인 필요)
    if (!userToken) {
        console.log('🚨 일반 사용자 토큰 없음 - 로그인 페이지로 이동');
        next('/login');
        return;
    }

    // 🔥 5. 그 외 모든 경우 통과
    next();
});


// 청크 로딩 오류 처리를 위한 전역 이벤트 리스너 추가
window.addEventListener('unhandledrejection', function(event) {
    if (event.reason && event.reason.message && event.reason.message.includes('Loading chunk')) {
        console.error('🚨 청크 로딩 오류 발생:', event.reason);
        // 캐시 삭제
        if ('caches' in window) {
            caches.keys().then(function(names) {
                for (let name of names) caches.delete(name);
            });
        }
        // 페이지 새로고침
        window.location.reload(true);
    }
});

export default router